import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Share2, Copy, MessageSquare, Twitter, Linkedin, Facebook, Check, ExternalLink } from 'lucide-react';
import { fetchApi, cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function ShareButton({ ticker, stockName, currentPrice, prediction, className }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [copied, setCopied] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [forumDialogOpen, setForumDialogOpen] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [creating, setCreating] = useState(false);

  const stockUrl = `${window.location.origin}/stocks/${ticker}`;
  const shareText = prediction 
    ? `Check out ${ticker} (${stockName}) on MarketMind - AI predicts ${prediction.predicted_change_pct > 0 ? '+' : ''}${prediction.predicted_change_pct?.toFixed(1)}% with ${prediction.confidence}% confidence!`
    : `Check out ${ticker} (${stockName}) on MarketMind for AI-powered stock predictions!`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(stockUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleShareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(stockUrl)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  const handleShareLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(stockUrl)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(stockUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  const handleOpenForumDialog = () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to post on the forum');
      navigate('/auth');
      return;
    }
    
    // Pre-fill the post with stock context
    const predictionText = prediction 
      ? `AI Prediction: ${prediction.predicted_change_pct > 0 ? '+' : ''}${prediction.predicted_change_pct?.toFixed(1)}% (${prediction.confidence}% confidence, ${prediction.time_horizon} timeframe)`
      : '';
    
    setNewPost({
      title: `Discussion: ${ticker} - ${stockName}`,
      content: predictionText ? `${predictionText}\n\nMy thoughts:\n` : 'My thoughts:\n'
    });
    setForumDialogOpen(true);
  };

  // Validate English-only text
  const isEnglishOnly = (text) => {
    const pattern = /^[a-zA-Z0-9\s\.\,\!\?\'\"\-\:\;\(\)\[\]\@\#\$\%\&\*\+\=\/\\\n\r]*$/;
    return pattern.test(text);
  };

  const handleCreateForumPost = async () => {
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }
    
    if (!isEnglishOnly(newPost.title) || !isEnglishOnly(newPost.content)) {
      toast.error('Posts must contain only English characters');
      return;
    }
    
    setCreating(true);
    try {
      const data = await fetchApi('/forum/posts', {
        method: 'POST',
        body: JSON.stringify({
          title: newPost.title,
          content: newPost.content,
          ticker: ticker
        })
      });
      toast.success('Posted to forum!');
      setForumDialogOpen(false);
      // Navigate to the new post
      navigate(`/forum/${data.post.post_id}`);
    } catch (error) {
      toast.error(error.message || 'Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            data-testid="share-stock-btn" 
            variant="outline" 
            size="sm"
            className={className}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={handleCopyLink} data-testid="copy-link-btn">
            {copied ? <Check className="w-4 h-4 mr-2 text-green-500" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={handleOpenForumDialog} data-testid="share-forum-btn">
            <MessageSquare className="w-4 h-4 mr-2" />
            Post to Forum
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={handleShareTwitter}>
            <Twitter className="w-4 h-4 mr-2" />
            Share on X
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleShareLinkedIn}>
            <Linkedin className="w-4 h-4 mr-2" />
            Share on LinkedIn
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleShareFacebook}>
            <Facebook className="w-4 h-4 mr-2" />
            Share on Facebook
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={() => navigate(`/forum?ticker=${ticker}`)}>
            <ExternalLink className="w-4 h-4 mr-2" />
            View Forum Discussions
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Forum Post Dialog */}
      <Dialog open={forumDialogOpen} onOpenChange={setForumDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Share to Forum</DialogTitle>
            <DialogDescription>
              Start a discussion about {ticker} with the community
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                data-testid="forum-post-title"
                value={newPost.title}
                onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                data-testid="forum-post-content"
                value={newPost.content}
                onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                rows={6}
                placeholder="Share your analysis, prediction, or question..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForumDialogOpen(false)}>Cancel</Button>
            <Button 
              data-testid="submit-forum-post"
              onClick={handleCreateForumPost} 
              disabled={creating}
            >
              {creating ? 'Posting...' : 'Post to Forum'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
